import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { BadgeCheck, Check, Copy, Download, FileText, Loader2, RotateCcw, Sparkles, Trash2 } from "lucide-react";
import closeIcon from "../../assets/cross.svg";
import { referenceLetterService } from "../../services/referenceLetterService";

/**
 * Draft → edit → export a reference letter for one student.
 *
 * Two screens. SETUP shows the student's record (the same evidence the model
 * will see) with checkboxes for what to feature, the letter's purpose, and a
 * notes box for what only the author knows. EDIT shows the drafted letter in
 * a textarea with Save / Copy / PDF, plus earlier letters for this student.
 *
 * Deliberately no "send" anywhere: the staff member owns where this goes.
 */

const PURPOSES = [
  { value: "COLLEGE", label: "College / university application" },
  { value: "SCHOLARSHIP", label: "Scholarship application" },
  { value: "EMPLOYMENT", label: "Job or internship" },
  { value: "GENERAL", label: "General character reference" },
];

const purposeLabel = (value) => PURPOSES.find((p) => p.value === value)?.label || value;

const formatDate = (iso, locale) =>
  iso ? new Date(iso).toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" }) : "";

const ReferenceLetterModal = ({ student, onClose }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || "en";
  const studentName = `${student?.firstName || ""} ${student?.lastName || ""}`.trim() || student?.slug || "Student";

  const [record, setRecord] = useState(null);
  const [recordError, setRecordError] = useState("");
  const [previous, setPrevious] = useState([]);

  const [purpose, setPurpose] = useState("COLLEGE");
  const [notes, setNotes] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [drafting, setDrafting] = useState(false);

  const [letter, setLetter] = useState(null); // the open letter
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loadingLetterId, setLoadingLetterId] = useState(null);
  const textareaRef = useRef(null);

  // Load the record + earlier letters together; the setup screen needs both.
  useEffect(() => {
    if (!student?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [rec, prev] = await Promise.all([
          referenceLetterService.getRecord(student.id),
          referenceLetterService.listForStudent(student.id).catch(() => []),
        ]);
        if (cancelled) return;
        setRecord(rec);
        setPrevious(prev);
        // Default pick mirrors the server's: featured, certified, or verified.
        const picked = rec.achievements.filter((a) => a.featured || a.certificate || a.hypes.some((h) => h.verified)).map((a) => a.id);
        setSelected(new Set(picked.length ? picked : rec.achievements.map((a) => a.id)));
      } catch (err) {
        if (!cancelled) setRecordError(err.message || "Couldn't load the student's record");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [student?.id]);

  const dirty = letter ? body !== letter.body : false;

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleDraft = async () => {
    if (!record) return;
    setDrafting(true);
    try {
      const created = await referenceLetterService.draft(student.id, {
        purpose,
        authorNotes: notes.trim() || undefined,
        featuredAchievementIds: Array.from(selected),
      });
      setLetter(created);
      setBody(created.body);
      setPrevious((prev) => [
        { id: created.id, purpose: created.purpose, authorName: record.author.name, createdAt: created.createdAt, updatedAt: created.updatedAt },
        ...prev,
      ]);
    } catch (err) {
      toast.error(err.message || "Couldn't draft the letter");
    } finally {
      setDrafting(false);
    }
  };

  const handleOpenPrevious = async (id) => {
    setLoadingLetterId(id);
    try {
      const full = await referenceLetterService.get(id);
      setLetter(full);
      setBody(full.body);
    } catch (err) {
      toast.error(err.message || "Couldn't open the letter");
    } finally {
      setLoadingLetterId(null);
    }
  };

  const handleSave = async () => {
    if (!letter || !dirty) return letter;
    setSaving(true);
    try {
      const saved = await referenceLetterService.update(letter.id, body);
      setLetter(saved);
      setPrevious((prev) => prev.map((p) => (p.id === saved.id ? { ...p, updatedAt: saved.updatedAt } : p)));
      toast.success("Letter saved");
      return saved;
    } catch (err) {
      toast.error(err.message || "Couldn't save the letter");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Older clipboard permission model: fall back to selecting the text.
      textareaRef.current?.select();
      toast.info("Press Ctrl/Cmd+C to copy");
    }
  };

  const handleDownload = async () => {
    if (!letter) return;
    setDownloading(true);
    try {
      // The PDF renders what's saved, so save first if edited.
      if (dirty) {
        const saved = await handleSave();
        if (!saved) return;
      }
      const { blob, filename } = await referenceLetterService.downloadPdf(letter.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Couldn't build the PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!letter) return;
    if (!window.confirm("Delete this letter? This can't be undone.")) return;
    try {
      await referenceLetterService.remove(letter.id);
      setPrevious((prev) => prev.filter((p) => p.id !== letter.id));
      setLetter(null);
      setBody("");
    } catch (err) {
      toast.error(err.message || "Couldn't delete the letter");
    }
  };

  const handleBackToSetup = () => {
    if (dirty && !window.confirm("You have unsaved edits. Leave without saving?")) return;
    setLetter(null);
    setBody("");
  };

  const handleClose = () => {
    if (dirty && !window.confirm("You have unsaved edits. Close without saving?")) return;
    onClose();
  };

  const verifiedCount = useMemo(
    () => (record ? record.achievements.filter((a) => a.certificate || a.hypes.some((h) => h.verified)).length : 0),
    [record],
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center p-4">
      <div className="w-full max-w-[960px] max-h-[92vh] bg-white rounded-[20px] overflow-hidden font-['Manrope'] flex flex-col">
        {/* Header */}
        <div className="px-6 py-[18px] bg-gradient-to-br from-white to-blue-100 border-b border-[#DAEBFF] flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-[18px] font-bold text-gray-900 flex items-center gap-2">
              <FileText size={18} className="text-blue-600" />
              {t("referenceLetter.title", { defaultValue: "Reference letter" })}
              <span className="text-gray-500 font-medium">· {studentName}</span>
            </h2>
            {record && !letter ? (
              <p className="text-xs text-gray-500 mt-0.5">
                {t("referenceLetter.subtitle", {
                  defaultValue: "Drafted only from what's on {{name}}'s Hypebook — {{count}} items, {{verified}} independently verified. You edit and sign it.",
                  name: record.student.name.split(" ")[0],
                  count: record.achievements.length,
                  verified: verifiedCount,
                })}
              </p>
            ) : null}
          </div>
          <button type="button" onClick={handleClose} className="p-0.5" aria-label={t("modals.common.close")}>
            <img src={closeIcon} alt="" className="w-[22px] h-[22px]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {recordError ? (
            <div className="p-8 text-center text-sm text-red-600">{recordError}</div>
          ) : !record ? (
            <div className="p-12 flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> {t("referenceLetter.loading", { defaultValue: "Loading record…" })}
            </div>
          ) : letter ? (
            /* ── EDIT ────────────────────────────────────────────── */
            <div className="p-6 flex flex-col gap-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{purposeLabel(letter.purpose)}</span>
                  <span className="mx-2 text-gray-300">·</span>
                  {t("referenceLetter.drafted", { defaultValue: "Drafted {{date}}", date: formatDate(letter.createdAt, locale) })}
                  {letter.edited || dirty ? (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
                      {dirty ? t("referenceLetter.unsaved", { defaultValue: "Unsaved edits" }) : t("referenceLetter.edited", { defaultValue: "Edited" })}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {body !== letter.aiDraft ? (
                    <button
                      type="button"
                      onClick={() => setBody(letter.aiDraft)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                      title="Put the original AI draft back in the editor (nothing is saved until you click Save)"
                    >
                      <RotateCcw size={14} /> {t("referenceLetter.resetToDraft", { defaultValue: "Reset to AI draft" })}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50 rounded-md"
                  >
                    <Trash2 size={14} /> {t("referenceLetter.delete", { defaultValue: "Delete" })}
                  </button>
                </div>
              </div>

              <textarea
                ref={textareaRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                spellCheck
                className="w-full min-h-[460px] p-5 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-[15px] leading-7 text-gray-900 font-serif resize-y"
              />

              <p className="text-xs text-gray-500 flex items-start gap-1.5">
                <Sparkles size={14} className="text-amber-500 shrink-0 mt-0.5" />
                {t("referenceLetter.disclaimer", {
                  defaultValue:
                    "AI-drafted from the student's record and your notes. Read it fully before you sign — check every claim, and add what only you know.",
                })}
              </p>
            </div>
          ) : (
            /* ── SETUP ───────────────────────────────────────────── */
            <div className="p-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
              {/* Left: record */}
              <div className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-bold text-gray-900">
                    {t("referenceLetter.featureHeading", { defaultValue: "What to feature" })}
                  </h3>
                  <div className="text-xs text-gray-500 flex gap-3">
                    <button type="button" className="hover:text-gray-900" onClick={() => setSelected(new Set(record.achievements.map((a) => a.id)))}>
                      {t("referenceLetter.selectAll", { defaultValue: "All" })}
                    </button>
                    <button type="button" className="hover:text-gray-900" onClick={() => setSelected(new Set())}>
                      {t("referenceLetter.selectNone", { defaultValue: "None" })}
                    </button>
                  </div>
                </div>
                {record.achievements.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 p-6 text-sm text-gray-500 text-center">
                    {t("referenceLetter.emptyRecord", {
                      defaultValue: "{{name}} hasn't added anything to their Hypebook yet, so there's nothing to write from.",
                      name: record.student.name.split(" ")[0],
                    })}
                  </div>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {record.achievements.map((a) => {
                      const isOn = selected.has(a.id);
                      const verifiedHypes = a.hypes.filter((h) => h.verified);
                      return (
                        <li key={a.id}>
                          <label
                            className={`flex gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                              isOn ? "border-blue-300 bg-blue-50/40" : "border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <input type="checkbox" checked={isOn} onChange={() => toggle(a.id)} className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-gray-900">{a.title}</span>
                                <span className="text-xs text-gray-500">{a.organization}</span>
                                {a.certificate ? (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">
                                    <BadgeCheck size={12} /> {t("referenceLetter.badgeCertificate", { defaultValue: "School certificate" })}
                                  </span>
                                ) : null}
                                {verifiedHypes.length ? (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">
                                    <BadgeCheck size={12} />{" "}
                                    {t("referenceLetter.badgeVerified", { defaultValue: "{{count}} verified", count: verifiedHypes.length })}
                                  </span>
                                ) : null}
                                {a.hypes.length > verifiedHypes.length ? (
                                  <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-medium">
                                    {t("referenceLetter.badgeUnverified", { defaultValue: "{{count}} unverified", count: a.hypes.length - verifiedHypes.length })}
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {a.type} · {a.startDate}
                                {a.ongoing ? " – present" : a.endDate ? ` – ${a.endDate}` : ""}
                              </div>
                              {a.description ? <p className="text-xs text-gray-600 mt-1 line-clamp-2">{a.description}</p> : null}
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {previous.length ? (
                  <div className="mt-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-2">
                      {t("referenceLetter.previousHeading", { defaultValue: "Earlier letters" })}
                    </h3>
                    <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
                      {previous.map((p) => (
                        <li key={p.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                          <div>
                            <span className="font-medium text-gray-900">{purposeLabel(p.purpose)}</span>
                            <span className="text-gray-500 text-xs ml-2">
                              {p.authorName} · {formatDate(p.updatedAt, locale)}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenPrevious(p.id)}
                            disabled={loadingLetterId === p.id}
                            className="text-blue-600 hover:underline text-xs font-medium disabled:opacity-50"
                          >
                            {loadingLetterId === p.id ? "…" : t("referenceLetter.open", { defaultValue: "Open" })}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              {/* Right: options */}
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-1.5">{t("referenceLetter.purpose", { defaultValue: "This letter is for" })}</label>
                  <div className="flex flex-col gap-1.5">
                    {PURPOSES.map((p) => (
                      <label
                        key={p.value}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                          purpose === p.value ? "border-blue-400 bg-blue-50 text-gray-900" : "border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <input type="radio" name="purpose" value={p.value} checked={purpose === p.value} onChange={() => setPurpose(p.value)} className="text-blue-600" />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-1">
                    {t("referenceLetter.notes", { defaultValue: "What only you know" })}
                  </label>
                  <p className="text-xs text-gray-500 mb-1.5">
                    {t("referenceLetter.notesHint", {
                      defaultValue: "How you know the student, and anything their record can't show. The letter won't invent this — if you leave it blank it stays out.",
                    })}
                  </p>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
                    rows={6}
                    placeholder="e.g. I taught Priya chemistry in grades 10 and 11. She stayed after class for weeks to get the titration lab right."
                    className="w-full p-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm text-gray-900 resize-y"
                  />
                  <div className="text-[11px] text-gray-400 text-right">{notes.length}/2000</div>
                </div>

                <button
                  type="button"
                  onClick={handleDraft}
                  disabled={drafting || record.achievements.length === 0 || selected.size === 0}
                  className="h-11 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {drafting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {drafting
                    ? t("referenceLetter.drafting", { defaultValue: "Writing… this takes a little while" })
                    : t("referenceLetter.draft", { defaultValue: "Draft the letter" })}
                </button>
                {selected.size === 0 && record.achievements.length > 0 ? (
                  <p className="text-xs text-amber-700 -mt-2">{t("referenceLetter.pickOne", { defaultValue: "Pick at least one item to feature." })}</p>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Footer (edit screen only) */}
        {letter ? (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3 shrink-0 bg-white">
            <button type="button" onClick={handleBackToSetup} className="text-sm font-medium text-gray-600 hover:text-gray-900">
              ← {t("referenceLetter.back", { defaultValue: "Start another" })}
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="h-10 inline-flex items-center gap-1.5 px-3 border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-800"
              >
                {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
                {copied ? t("referenceLetter.copied", { defaultValue: "Copied" }) : t("referenceLetter.copy", { defaultValue: "Copy text" })}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="h-10 inline-flex items-center gap-1.5 px-3 border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-800 disabled:opacity-50"
              >
                {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                {t("referenceLetter.pdf", { defaultValue: "Download PDF" })}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="h-10 inline-flex items-center gap-1.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg text-sm font-semibold"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : null}
                {t("referenceLetter.save", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ReferenceLetterModal;
